import React, {useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Navbar from "../Navbar/Nabvar";
import Footer from '../Footer/Footer';
import {
    Box,
    Typography,
    Container,
    Card,
    CardContent,
    CardMedia,
    IconButton,
    Grid,
    styled,
    Button
} from '@mui/material';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import mock_trends from './mock_trends';
import SubtrendDialog from './SubtrendDialog';
import QuoteBox from '../Quote/QuoteBox';
import {getBaseUrl} from "../Config/BaseUrl";

interface Subtrend {
    id: string;
    title: string;
    text: string;
    image: string;
}

const primaryBlue = '#1F1B9E';

const MainContent = styled(Box)({
    position: 'relative',
    '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: `
            linear-gradient(to bottom right, 
                rgba(240, 245, 255, 0.9),
                rgba(230, 240, 255, 0.9)
            )
        `,
        zIndex: 0
    }
});

const ContentWrapper = styled(Box)({
    position: 'relative',
    zIndex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: '100%',
    margin: '0',
    padding: '0',
});

const NavigationSection = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
    padding: theme.spacing(4),
    marginBottom: theme.spacing(4),
    '& .MegatrendCard': {
        width: '400px',
        height: '400px', // Geändert zu quadratisch
    },
    '& .navigation-left': {
        zIndex: 1
    },
    '& .navigation-right': {
        zIndex: 1
    },
    // Improved responsive styling
    [theme.breakpoints.down('md')]: {
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: theme.spacing(3),
        width: '100%',
        padding: theme.spacing(2),
        '& .MegatrendCard': {
            width: '100%',
            maxWidth: '400px',
            height: '400px', // Auch responsive quadratisch
            margin: '0 auto',
        }
    }
}));

const NavigationButton = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    padding: theme.spacing(2),

    '&:hover': {
        transform: 'scale(1.05)',
        '& .navigation-arrow': {
            // @ts-ignore
            transform: props => props.direction === 'left'
                ? 'rotate(180deg) translateX(-5px)'
                : 'translateX(5px)',
        },
        '& .navigation-text': {
            // @ts-ignore
            transform: props => props.direction === 'left'
                ? 'translateX(-5px)'
                : 'translateX(5px)',
        }
    },

    '&.disabled': {
        opacity: 0.5,
        cursor: 'default',
        '&:hover': {
            transform: 'none',
            '& .navigation-arrow, & .navigation-text': {
                transform: 'none'
            }
        }
    },

    '& .navigation-arrow': {
        color: primaryBlue,
        fontSize: '3rem', // Increased arrow size
        transition: 'transform 0.3s ease',
    },

    '& .navigation-text': {
        color: primaryBlue,
        fontSize: '1.5rem', // Increased text size
        fontWeight: 600,
        transition: 'transform 0.3s ease'
    },

    // Add responsive styling
    [theme.breakpoints.down('sm')]: {
        '& .navigation-arrow': {
            fontSize: '2rem',
        },
        '& .navigation-text': {
            fontSize: '1.2rem',
        }
    },
    [theme.breakpoints.down('md')]: {
        width: '100%',
        maxWidth: '400px',
        justifyContent: 'center'
    }
}));

const SubtrendCard = styled(Card)(({ theme }) => ({
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    transition: 'transform 0.2s',
    '&:hover': {
        transform: 'scale(1.02)',
        cursor: 'pointer'
    }
}));

const MegatrendDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const trends = mock_trends;
    const [selectedSubtrend, setSelectedSubtrend] = useState<Subtrend | null>(null);

    React.useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const currentTrend = trends.find(trend => trend.id === id);
    const currentIndex = trends.findIndex(trend => trend.id === id);
    const prevTrend = currentIndex > 0 ? trends[currentIndex - 1] : null;
    const nextTrend = currentIndex < trends.length - 1 ? trends[currentIndex + 1] : null;

    if (!currentTrend) {
        return <Typography>Megatrend nicht gefunden</Typography>;
    }

    const itemsPerRow = 4;

    return (
        <>
            <Navbar />
            <MainContent>
                <ContentWrapper>
                    <Container maxWidth="xl">
                        <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                            <img
                                src="../old/search-removebg-preview.png"
                                alt="Logo"
                                style={{
                                    maxHeight: 700,
                                    width: '100%',
                                    maxWidth: '400px',
                                    height: 'auto',
                                    margin: '0 auto'
                                }}
                            />

                            <Typography
                                variant="body1"
                                sx={{
                                    color: primaryBlue,
                                    maxWidth: '1300px',
                                    mx: 'auto',
                                    px: 2,
                                    fontSize: {xs: '1.2rem', sm: '1.4rem', md: '1.6rem'},
                                    mb: 3,
                                    textAlign: 'center'
                                }}
                            >
                                Geschäftsmodelltrends sind aktuelle Entwicklungen und Veränderungen in der Art und Weise, wie Unternehmen Wert schaffen, liefern und monetarisieren. Sie entstehen durch technologische Innovationen, verändertes Konsumverhalten, neue Marktbedingungen oder gesellschaftliche Veränderungen.
                            </Typography>

                        </Box>

                        <NavigationSection>
                            <NavigationButton
                                className={`navigation-left ${!prevTrend ? 'disabled' : ''}`}
                                onClick={() => prevTrend && navigate(`/megatrend/${prevTrend.id}`)}
                                // @ts-ignore
                                direction="left"
                            >
                                <ArrowForwardIosIcon
                                    className="navigation-arrow"
                                    sx={{
                                        transform: 'rotate(180deg)',
                                    }}
                                />
                                <Typography
                                    className="navigation-text"
                                >
                                    voriger Megatrend
                                </Typography>
                            </NavigationButton>

                            <Box className="MegatrendCard" sx={{
                                position: 'relative',
                                border: `2px solid ${currentTrend.color}`,
                                borderRadius: '8px',
                                overflow: 'hidden',
                                width: { xs: '90%', sm: '90%', md: '400px' },
                                height: { xs: '90vw', sm: '90vw', md: '400px' }, // Quadratisch auch responsive
                                maxWidth: '400px',
                                maxHeight: '400px',
                                aspectRatio: '1 / 1', // Erzwingt quadratisches Verhältnis
                                margin: { xs: '0 auto', md: '0' }
                            }}>
                                <CardMedia
                                    component="img"
                                    image={"../../" + currentTrend.image}
                                    alt={currentTrend.title}
                                    sx={{
                                        width: '100%',
                                        height: { xs: '80%', sm: '78%', md: '75%' }, // Mehr Platz für Bild durch kleinere Textbox
                                        objectFit: 'cover',
                                        objectPosition: 'top'
                                    }}
                                />

                                <Box sx={{
                                    position: 'absolute',
                                    bottom: 0,
                                    left: 0,
                                    right: 0,
                                    height: { xs: '20%', sm: '22%', md: '25%' }, // Responsive kleinere Höhe
                                    bgcolor: currentTrend.color,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    p: { xs: 0.5, sm: 0.8, md: 1 }, // Responsive Padding
                                    boxSizing: 'border-box'
                                }}>
                                    <Typography
                                        variant="h4"
                                        sx={{
                                            color: 'white',
                                            textAlign: 'center',
                                            fontSize: { xs: '1rem', sm: '1.3rem', md: '1.6rem' }, // Kleinere Schrift
                                            fontWeight: 'bold',
                                            lineHeight: 1.1, // Kompaktere Zeilenhöhe
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            display: '-webkit-box',
                                            WebkitLineClamp: { xs: 2, sm: 2, md: 3 }, // Weniger Zeilen auf kleinen Geräten
                                            WebkitBoxOrient: 'vertical',
                                            wordBreak: 'break-word',
                                            hyphens: 'auto'
                                        }}
                                    >
                                        {currentTrend.title}
                                    </Typography>
                                </Box>
                            </Box>

                            <NavigationButton
                                className={`navigation-right ${!nextTrend ? 'disabled' : ''}`}
                                onClick={() => nextTrend && navigate(`/megatrend/${nextTrend.id}`)}
                                // @ts-ignore
                                direction="right"
                            >
                                <Typography
                                    className="navigation-text"
                                >
                                    nächster Megatrend
                                </Typography>
                                <ArrowForwardIosIcon
                                    className="navigation-arrow"
                                />
                            </NavigationButton>
                        </NavigationSection>

                        <Box
                            sx={{
                                mb: 8,
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                textAlign: 'center'
                            }}
                        >
                            <Typography
                                variant="h2"
                                sx={{
                                    color: primaryBlue,
                                    textAlign: 'center',
                                    mb: 4,
                                    fontSize: {xs: '2rem', sm: '2.5rem', md: '3rem'}
                                }}
                            >
                                Subtrends
                            </Typography>

                            <Typography
                                variant="body1"
                                sx={{
                                    color: primaryBlue,
                                    textAlign: 'center',
                                    mb: 6,
                                    fontSize: {xs: '1.2rem', sm: '1.4rem', md: '1.6rem'}
                                }}
                            >
                                Klicken Sie auf die Kacheln für Details zu den Geschäftsmodell-Subtrends.
                            </Typography>

                            <Grid container spacing={4} sx={{ justifyContent: 'center' }}>
                                {currentTrend.subtrends.map((subtrend) => (
                                    <Grid item xs={12} sm={6} md={4} lg={3} key={subtrend.id}>
                                        <SubtrendCard
                                            onClick={() => setSelectedSubtrend(subtrend)}
                                            sx={{
                                                border: `2px solid ${currentTrend.color}`,
                                                aspectRatio: '1 / 1',
                                                display: 'flex',
                                                position: 'relative',
                                                overflow: 'hidden'
                                            }}
                                        >
                                            <CardMedia
                                                component="img"
                                                image={subtrend.image}
                                                alt={subtrend.title}
                                                sx={{
                                                    width: '100%',
                                                    height: { xs: '80%', sm: '78%', md: '75%' }, // Mehr Platz für Bild
                                                    objectFit: 'cover',
                                                    objectPosition: 'top'
                                                }}
                                            />
                                            <Box
                                                sx={{
                                                    position: 'absolute',
                                                    bottom: 0,
                                                    left: 0,
                                                    right: 0,
                                                    height: { xs: '20%', sm: '22%', md: '25%' }, // Responsive kleinere Höhe auch für Subtrends
                                                    bgcolor: currentTrend.color,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    p: { xs: 0.5, sm: 0.8, md: 1 },
                                                    boxSizing: 'border-box'
                                                }}
                                            >
                                                <Typography
                                                    variant="h6"
                                                    sx={{
                                                        color: 'white',
                                                        textAlign: 'center',
                                                        fontSize: { xs: '0.9rem', sm: '1rem', md: '1.3rem' }, // Kleinere Schrift für Subtrends
                                                        fontWeight: 'bold',
                                                        lineHeight: 1.1,
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        display: '-webkit-box',
                                                        WebkitLineClamp: { xs: 1, sm: 2, md: 2 }, // 1 Zeile auf Handy, 2 auf größeren Geräten
                                                        WebkitBoxOrient: 'vertical',
                                                        wordBreak: 'break-word',
                                                        hyphens: 'auto'
                                                    }}
                                                >
                                                    {subtrend.title}
                                                </Typography>
                                            </Box>
                                        </SubtrendCard>
                                    </Grid>
                                ))}
                            </Grid>

                            {selectedSubtrend && (
                                <SubtrendDialog
                                    open={!!selectedSubtrend}
                                    onClose={() => setSelectedSubtrend(null)}
                                    subtrend={selectedSubtrend}
                                />
                            )}

                            <Button
                                variant="outlined"
                                sx={{
                                    mt: 4,
                                    color: primaryBlue,
                                    borderColor: primaryBlue,
                                    borderRadius: '24px',
                                    padding: '8px 32px',
                                    fontSize: '1.1rem',
                                    '&:hover': {
                                        borderColor: primaryBlue,
                                        backgroundColor: 'rgba(31, 27, 158, 0.04)'
                                    },
                                    marginBottom: 10
                                }}
                                onClick={() => (window.location.href = `${getBaseUrl()}/Trends`)}
                            >
                                zurück zu allen<br />
                                Megatrends
                            </Button>
                        </Box>

                    </Container>
                </ContentWrapper>
            </MainContent>

            <QuoteBox
                text="„Die Zukunft kann man am besten voraussagen, wenn man sie selbst gestaltet.“"
                maxWidth="1200px"
                hasOverlay={true}
                author={"Alan Kay"}
            />

            <Footer />
        </>
    );
};

export default MegatrendDetail;